/**
 * <s-scroll-restore> — Returns shoppers to the exact spot they left when they press Back
 * (or reload), on every page of the store, on desktop and mobile.
 *
 * Browsers try to restore scroll natively, but when a page is re-rendered the restore runs
 * before images and product grids have their full height, so it lands at or near the top.
 * Mobile browsers (iOS Safari, Android Chrome) also often restore pages from the
 * back/forward cache and can drop the position there. This component remembers the scroll
 * position of each URL in sessionStorage and re-applies it in both cases while the page
 * finishes laying out.
 *
 * - Restoring stops as soon as the shopper scrolls or presses a key themselves.
 * - The key includes the query string, so filtered/sorted/paginated collection URLs each
 *   keep their own position.
 * - Uses the two-argument scrollTo with scroll-behavior forced to auto, because older
 *   mobile Safari throws on `behavior: 'instant'`.
 *
 * Render once in the layout, outside design mode.
 */
const STORAGE_PREFIX = 'theme-scroll:';
const MAX_RESTORE_MS = 6000;

class SScrollRestore extends HTMLElement {
  #ac;
  #saveTimer;
  #restoring = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;

    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch {
      // Ignore: native restoration stays on.
    }

    window.addEventListener(
      'scroll',
      () => {
        if (this.#restoring) return;
        clearTimeout(this.#saveTimer);
        this.#saveTimer = setTimeout(() => this.#save(), 100);
      },
      { passive: true, signal }
    );
    window.addEventListener('pagehide', () => this.#save(), { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.#save();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('a[href]')) this.#save();
    }, { capture: true, signal });

    // Back/forward cache restores (very common on phones): re-apply the saved position.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.#restore();
    }, { signal });

    let navType = '';
    try {
      navType = performance.getEntriesByType('navigation')[0]?.type ?? '';
    } catch {
      navType = '';
    }
    // Legacy fallback for browsers without Navigation Timing Level 2.
    if (!navType && performance.navigation) {
      navType = { 1: 'reload', 2: 'back_forward' }[performance.navigation.type] ?? '';
    }
    if (navType === 'back_forward' || navType === 'reload') this.#restore();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#restoring?.abort();
    clearTimeout(this.#saveTimer);
  }

  #key() {
    return STORAGE_PREFIX + location.pathname + location.search;
  }

  #currentY() {
    return Math.round(window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0);
  }

  #save() {
    if (this.#restoring) return;
    try {
      sessionStorage.setItem(this.#key(), String(this.#currentY()));
    } catch {
      // Storage can be unavailable (private mode, quota); fall back to browser behavior.
    }
  }

  #jump(y) {
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    root.style.scrollBehavior = previous;
  }

  #restore() {
    let target;
    try {
      target = parseInt(sessionStorage.getItem(this.#key()) ?? '', 10);
    } catch {
      return;
    }
    if (!Number.isFinite(target) || target <= 0) return;

    this.#restoring?.abort();
    const stop = new AbortController();
    this.#restoring = stop;
    stop.signal.addEventListener('abort', () => {
      if (this.#restoring === stop) this.#restoring = null;
    });

    // Only deliberate shopper input cancels; a stray tap (or the end of a swipe-back
    // gesture) must not.
    const cancel = () => stop.abort();
    for (const type of ['wheel', 'touchmove', 'keydown']) {
      window.addEventListener(type, cancel, { once: true, passive: true, signal: stop.signal });
    }

    const started = performance.now();
    let loaded = document.readyState === 'complete';
    if (!loaded) window.addEventListener('load', () => { loaded = true; }, { once: true, signal: stop.signal });
    let settledFrames = 0;

    const step = () => {
      if (stop.signal.aborted) return;
      const scroller = document.scrollingElement || document.documentElement;
      const maxScroll = scroller.scrollHeight - window.innerHeight;
      const y = Math.min(target, Math.max(0, maxScroll));
      if (Math.abs(this.#currentY() - y) > 2) this.#jump(y);

      const reached = maxScroll >= target - 2 && Math.abs(this.#currentY() - target) <= 2;
      settledFrames = reached ? settledFrames + 1 : 0;

      // Hold the position until the page has fully loaded and stayed put briefly, so late
      // images, fonts or the mobile address bar cannot push the shopper away.
      if ((loaded && settledFrames > 15) || performance.now() - started > MAX_RESTORE_MS) {
        stop.abort();
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

customElements.define('s-scroll-restore', SScrollRestore);
